/**
 * Integrations state — backed by the real backend (`GET user-integrations`),
 * shared across the list + detail pages via React Query. Connected state is
 * derived by matching each catalog tool's `id` to a user-integration `provider`.
 */

import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  connectIntegration,
  disconnectIntegration,
  listUserIntegrations,
  type UserIntegration,
} from './integrationsApi';

const USER_INTEGRATIONS_KEY = ['user-integrations'] as const;

export interface UseIntegrations {
  /** Raw connected rows from the backend. */
  connections: UserIntegration[];
  isLoading: boolean;
  refetch: () => void;
  /** The connected row for a provider (catalog id), if any. */
  getConnection: (provider: string) => UserIntegration | undefined;
  isConnected: (provider: string) => boolean;
  /** Fetch the broker consent URL and redirect the browser to it. */
  connect: (provider: string) => Promise<void>;
  /** Disconnect + refresh the list. */
  disconnect: (userIntegrationId: string) => Promise<void>;
}

export function useIntegrations(): UseIntegrations {
  const queryClient = useQueryClient();
  const { data = [], isLoading, refetch } = useQuery<UserIntegration[]>({
    queryKey: USER_INTEGRATIONS_KEY,
    queryFn: listUserIntegrations,
    staleTime: 60_000,
  });

  const byProvider = useMemo(
    () => new Map(data.map((i) => [i.provider, i])),
    [data]
  );

  return {
    connections: data,
    isLoading,
    refetch,
    getConnection: (provider) => byProvider.get(provider),
    isConnected: (provider) => byProvider.has(provider),
    connect: connectIntegration,
    disconnect: async (userIntegrationId) => {
      await disconnectIntegration(userIntegrationId);
      await queryClient.invalidateQueries({ queryKey: USER_INTEGRATIONS_KEY });
    },
  };
}
